import { CaptureWorkerClient } from "./worker-client";
import type { WorkerEvent } from "./worker-protocol";
import type { CaptureMeta, CaptureSnapshot, LocalTaskDraft } from "./types";

interface WorkletChunk {
  type: "chunk";
  sequence: number;
  firstSample: number;
  frameCount: number;
  sampleRate: number;
  bytes: ArrayBuffer;
}
const INITIAL: CaptureSnapshot = {
  captures: [],
  drafts: [],
  activeId: null,
  receivedFrames: 0,
  pendingLocalChunks: 0,
  busy: false,
  syncing: false,
  error: null,
};
export function recordingSupport(): string | null {
  if (!globalThis.isSecureContext)
    return "Для микрофона нужен HTTPS или локальный адрес. Загрузка файла доступна отдельно.";
  if (
    !navigator.mediaDevices?.getUserMedia ||
    typeof AudioContext === "undefined" ||
    typeof AudioWorkletNode === "undefined" ||
    typeof Worker === "undefined" ||
    !globalThis.indexedDB
  )
    return "Этот браузер не поддерживает надёжную запись. Используйте актуальный браузер или загрузите аудиофайл.";
  if (!navigator.locks)
    return "Браузер не поддерживает защиту записи между вкладками. Можно загрузить готовый файл.";
  return null;
}
export function microphoneError(error: unknown): string {
  const name = error instanceof DOMException ? error.name : "";
  if (name === "NotAllowedError" || name === "SecurityError")
    return "Доступ к микрофону не предоставлен. Разрешите его в настройках сайта и нажмите «Записать» снова.";
  if (name === "NotFoundError")
    return "Микрофон не найден. Подключите его или загрузите готовый файл.";
  if (name === "NotReadableError")
    return "Микрофон занят или недоступен. Закройте другое приложение записи и попробуйте снова.";
  return error instanceof Error
    ? error.message
    : "Не удалось начать запись. Уже сохранённое аудио осталось на устройстве.";
}

/** Lives above task routes. Navigation does not own the audio graph or uploader. */
export class CaptureManager {
  private snapshot: CaptureSnapshot = INITIAL;
  private readonly listeners = new Set<() => void>();
  private readonly client: CaptureWorkerClient;
  private readonly ready: Promise<void>;
  private stream?: MediaStream;
  private context?: AudioContext;
  private node?: AudioWorkletNode;
  private releaseLease?: () => void;
  private boundary?: {
    resolve: (frames: number) => void;
    reject: (error: Error) => void;
  };
  private readonly localWaiters = new Set<() => void>();
  private uploadTimer?: ReturnType<typeof setTimeout>;
  private uploadBlocked = false;
  private closed = false;
  private lifetime = 0;
  private readonly uploading = new Set<string>();
  private readonly uploadPromises = new Map<string, Promise<void>>();
  private readonly scheduledUploads = new Set<string>();
  private readonly serverSyncing = new Set<string>();

  constructor(
    readonly accountId: string,
    private readonly csrf: () => string,
    private readonly changed: () => void = () => {},
  ) {
    this.client = new CaptureWorkerClient(
      new Worker(new URL("./capture.worker.ts", import.meta.url), {
        type: "module",
      }),
      (event) => this.onEvent(event),
    );
    this.ready = this.client
      .call<CaptureMeta[]>({ type: "init", accountId })
      .then(async (captures) => {
        const drafts = await this.client.call<LocalTaskDraft[]>({
          type: "drafts",
        });
        this.patch({ captures, drafts });
        if (navigator.onLine)
          for (const capture of captures)
            if (capture.state !== "finished") this.scheduleUpload(capture.id);
      })
      .catch((error) => {
        this.patch({ error: microphoneError(error) });
        throw error;
      });
    // Readiness failure is visible, while individual actions still reject.
    void this.ready.catch(() => {});
    window.addEventListener("online", this.onOnline);
    window.addEventListener("beforeunload", this.beforeUnload);
  }
  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };
  getSnapshot = (): CaptureSnapshot => this.snapshot;
  private patch(next: Partial<CaptureSnapshot>): void {
    this.snapshot = { ...this.snapshot, ...next };
    for (const listener of this.listeners) listener();
    for (const wake of this.localWaiters) wake();
  }
  private replace(meta: CaptureMeta): void {
    const captures = this.snapshot.captures.filter(
      (capture) => capture.id !== meta.id,
    );
    this.patch({ captures: [meta, ...captures] });
  }
  private onEvent(event: WorkerEvent): void {
    if (event.type === "committed") {
      this.replace(event.meta);
      this.patch({ pendingLocalChunks: event.pending });
      if (event.captureId === this.snapshot.activeId)
        this.node?.port.postMessage({
          type: "committed",
          sequence: event.sequence,
        });
      this.scheduleUpload(event.captureId);
    }
    if (event.type === "acknowledged") this.replace(event.meta);
    if (event.type === "sync-state") {
      if (event.syncing) this.serverSyncing.add(event.captureId);
      else this.serverSyncing.delete(event.captureId);
      this.patch({ syncing: this.serverSyncing.size > 0 });
    }
    if (event.type === "storage-error") {
      this.patch({ error: event.error, pendingLocalChunks: event.pending });
      void this.pause("storage_error").catch(() => {});
    }
  }
  async createDraft(
    title: string,
    mode: "after" | "live",
    id = crypto.randomUUID(),
  ): Promise<LocalTaskDraft> {
    await this.ready;
    const draft = {
      id,
      accountId: this.accountId,
      title,
      captureMode: mode,
      createdAt: new Date().toISOString(),
    };
    await this.client.call({ type: "draft-create", draft });
    this.patch({
      drafts: [draft, ...this.snapshot.drafts.filter((d) => d.id !== id)],
    });
    return draft;
  }
  private async acquireLease(): Promise<void> {
    const acquired = new Promise<void>((resolve, reject) => {
      void navigator.locks
        .request(
          "clones-studio-microphone",
          { ifAvailable: true, mode: "exclusive" },
          async (lock) => {
            if (!lock) {
              reject(
                new Error(
                  "В другой вкладке уже идёт запись. Поставьте её на паузу и вернитесь сюда.",
                ),
              );
              return;
            }
            await new Promise<void>((release) => {
              this.releaseLease = release;
              resolve();
            });
          },
        )
        .catch(reject);
    });
    await acquired;
  }
  async record(
    task: { id: string; title: string; capture_mode: "after" | "live" },
    resumeId?: string,
  ): Promise<void> {
    if (this.snapshot.busy || this.snapshot.activeId)
      throw new Error("Сначала поставьте текущую запись на паузу");
    if (this.snapshot.pendingLocalChunks > 0)
      throw new Error(
        "Сначала повторите сохранение несохранённого звука на устройстве",
      );
    const startedAt = new Date().toISOString();
    const unsupported = recordingSupport();
    if (unsupported) throw new Error(unsupported);
    this.patch({ busy: true, error: null });
    try {
      await this.ready;
      if (this.closed) throw new Error("Сессия записи закрыта");
      await this.acquireLease();
      // Only this explicit Record/Resume action requests permission.
      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false,
        },
      });
      if (this.closed) throw new Error("Сессия записи закрыта");
      this.context = new AudioContext();
      this.context.onstatechange = () => {
        if (
          this.context?.state === "suspended" &&
          this.snapshot.activeId &&
          !this.snapshot.busy
        )
          this.interrupted();
      };
      for (const track of this.stream.getAudioTracks())
        track.onended = () => this.interrupted();
      const sampleRate = this.context.sampleRate;
      if (
        !Number.isInteger(sampleRate) ||
        sampleRate < 8000 ||
        sampleRate > 96000
      )
        throw new Error(
          "Не удалось настроить микрофон. Попробуйте другой микрофон или загрузите готовую запись.",
        );
      let meta = resumeId
        ? this.snapshot.captures.find((capture) => capture.id === resumeId)
        : undefined;
      if (
        resumeId &&
        (!meta ||
          meta.taskId !== task.id ||
          meta.accountId !== this.accountId ||
          meta.state === "finished")
      )
        throw new Error("Эта локальная запись недоступна для продолжения");
      if (meta && meta.sampleRate !== sampleRate)
        throw new Error(
          "Частота микрофона изменилась. Прежнее аудио сохранено; создайте новую задачу для продолжения, чтобы не смешивать записи.",
        );
      if (!meta) {
        const now = new Date().toISOString();
        meta = {
          id: crypto.randomUUID(),
          accountId: this.accountId,
          taskId: task.id,
          taskTitle: task.title,
          captureMode: task.capture_mode,
          startedAt,
          sampleRate,
          channels: 1,
          encoding: "pcm_s16le_v1",
          nextSequence: 0,
          localFrames: 0,
          acknowledgedSequence: 0,
          acknowledgedFrames: 0,
          state: "recording",
          createdAt: now,
          updatedAt: now,
        };
        await this.client.call({ type: "create", meta });
      } else {
        meta = await this.client.call<CaptureMeta>({
          type: "state",
          captureId: meta.id,
          state: "recording",
          interrupted: meta.state === "recording" || meta.interrupted,
        });
      }
      this.replace(meta);
      await this.context.audioWorklet.addModule(
        `${import.meta.env.BASE_URL}pcm-capture.worklet.js`,
      );
      if (this.closed) throw new Error("Сессия записи закрыта");
      this.node = new AudioWorkletNode(this.context, "studio-pcm-capture", {
        numberOfInputs: 1,
        numberOfOutputs: 1,
        outputChannelCount: [1],
        processorOptions: {
          sequence: meta.nextSequence,
          firstSample: meta.localFrames,
        },
      });
      const captureId = meta.id;
      this.node.port.onmessage = ({
        data,
      }: MessageEvent<
        | WorkletChunk
        | { type: "boundary" | "backpressure"; frames: number }
        | { type: "meter"; frames: number; peak: number }
      >) => {
        if (data.type === "chunk") {
          this.patch({
            receivedFrames: data.firstSample + data.frameCount,
            pendingLocalChunks: this.snapshot.pendingLocalChunks + 1,
          });
          void this.client
            .call(
              {
                type: "append",
                chunk: {
                  accountId: this.accountId,
                  captureId,
                  sequence: data.sequence,
                  firstSample: data.firstSample,
                  frameCount: data.frameCount,
                  sampleRate: data.sampleRate,
                  bytes: data.bytes,
                },
              },
              [data.bytes],
            )
            .catch((error) => {
              this.patch({ error: microphoneError(error) });
            });
        } else if (data.type === "meter") {
          this.patch({ receivedFrames: data.frames, audioLevel: data.peak });
        } else if (data.type === "boundary") {
          this.boundary?.resolve(data.frames);
          this.boundary = undefined;
        } else {
          this.patch({
            error:
              "Устройство не успевает сохранять звук. Запись остановлена; дождитесь локального сохранения и продолжите.",
          });
          void this.pause("storage_error").catch(() => {});
        }
      };
      this.context.createMediaStreamSource(this.stream).connect(this.node);
      this.node.connect(this.context.destination);
      await this.context.resume();
      this.patch({
        activeId: captureId,
        receivedFrames: meta.localFrames,
        pendingLocalChunks: 0,
      });
      this.node.port.postMessage({ type: "record" });
      void navigator.storage?.persist?.().catch(() => false);
      void navigator.storage
        ?.estimate?.()
        .then((estimate) =>
          this.patch({
            storageUsage: estimate.usage,
            storageQuota: estimate.quota,
          }),
        )
        .catch(() => {});
    } catch (error) {
      await this.releaseAudio();
      this.patch({ activeId: null, error: microphoneError(error) });
      throw new Error(microphoneError(error));
    } finally {
      this.patch({ busy: false });
    }
  }
  private interrupted(): void {
    if (!this.snapshot.activeId || this.snapshot.busy) return;
    this.patch({
      error:
        "Запись с микрофона прервалась. Сохранённая часть доступна; прослушайте её перед продолжением.",
    });
    void this.pause("storage_error").catch(() => {});
  }
  private waitForLocal(id: string, frames: number): Promise<void> {
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        this.localWaiters.delete(check);
        reject(
          new Error(
            "Не весь звук сохранён на устройстве. Не закрывайте вкладку: повторите локальное сохранение.",
          ),
        );
      }, 10_000);
      const check = () => {
        const meta = this.snapshot.captures.find(
          (capture) => capture.id === id,
        );
        if (
          meta &&
          meta.localFrames === frames &&
          this.snapshot.pendingLocalChunks === 0
        ) {
          clearTimeout(timer);
          this.localWaiters.delete(check);
          resolve();
        }
      };
      this.localWaiters.add(check);
      check();
    });
  }
  async pause(
    state: "paused" | "stopped" | "storage_error" = "paused",
  ): Promise<void> {
    const id = this.snapshot.activeId;
    if (!id || this.snapshot.busy) return;
    this.patch({ busy: true });
    let frames = this.snapshot.receivedFrames;
    try {
      if (this.node) {
        frames = await new Promise<number>((resolve, reject) => {
          const timer = setTimeout(() => {
            this.boundary = undefined;
            reject(
              new Error(
                "Не удалось подтвердить остановку микрофона. Сохранённая часть записи доступна; не закрывайте вкладку.",
              ),
            );
          }, 3000);
          this.boundary = {
            resolve: (value) => {
              clearTimeout(timer);
              resolve(value);
            },
            reject,
          };
          this.node!.port.postMessage({ type: "pause" });
        });
      }
      await this.releaseAudio();
      await this.waitForLocal(id, frames);
      const meta = await this.client.call<CaptureMeta>({
        type: "state",
        captureId: id,
        state,
      });
      this.replace(meta);
      this.patch({ activeId: null });
      this.scheduleUpload(id);
    } catch (error) {
      await this.releaseAudio();
      this.patch({ activeId: null, error: microphoneError(error) });
      const meta = await this.client
        .call<CaptureMeta>({
          type: "state",
          captureId: id,
          state: "storage_error",
          interrupted: true,
        })
        .catch(() => undefined);
      if (meta) this.replace(meta);
      throw error;
    } finally {
      this.patch({ busy: false });
    }
  }
  async retryLocal(): Promise<void> {
    await this.ready;
    const captures = await this.client.call<CaptureMeta[]>({
      type: "retry-local",
    });
    this.patch({ captures, pendingLocalChunks: 0, error: null });
  }
  async finish(id: string): Promise<void> {
    const clicked = new Date().toISOString();
    const active = this.snapshot.activeId === id;
    if (active) await this.pause("stopped");
    if (this.snapshot.pendingLocalChunks > 0)
      throw new Error("Сначала сохраните оставшуюся часть записи");
    const capture = this.snapshot.captures.find((row) => row.id === id);
    if (!capture) throw new Error("Локальная запись не найдена");
    if (capture.state === "finished") return;
    const meta = await this.client.call<CaptureMeta>({
      type: "state",
      captureId: id,
      state: "stopped",
      completedAt:
        capture.completedAt !== undefined
          ? capture.completedAt
          : !active && (capture.interrupted || capture.state === "recording")
            ? null
            : clicked,
    });
    this.replace(meta);
    await this.synchronize(id, true);
  }
  async synchronize(id: string, seal = false): Promise<void> {
    const current = this.uploadPromises.get(id);
    if (current) {
      await current;
      if (!seal) return;
    }
    const promise = this.upload(id, seal);
    this.uploadPromises.set(id, promise);
    try {
      await promise;
    } finally {
      if (this.uploadPromises.get(id) === promise)
        this.uploadPromises.delete(id);
    }
  }
  private async upload(id: string, seal: boolean): Promise<void> {
    await this.ready;
    this.uploading.add(id);
    try {
      const meta = await this.client.call<CaptureMeta>({
        type: "sync",
        captureId: id,
        csrf: this.csrf(),
        seal,
      });
      this.replace(meta);
      this.uploadBlocked = false;
      this.patch({
        drafts: await this.client.call<LocalTaskDraft[]>({ type: "drafts" }),
      });
      this.changed();
    } catch (error) {
      const status = (error as { status?: number }).status;
      if (status && status < 500) this.uploadBlocked = true;
      this.patch({ error: microphoneError(error) });
      throw error;
    } finally {
      this.uploading.delete(id);
    }
  }
  private scheduleUpload(id: string): void {
    this.scheduledUploads.add(id);
    if (this.closed || this.uploadBlocked || this.uploadTimer) return;
    this.uploadTimer = setTimeout(() => {
      this.uploadTimer = undefined;
      const ids = Array.from(this.scheduledUploads);
      this.scheduledUploads.clear();
      for (const next of ids)
        void this.synchronize(next).catch(() => {
          if (!this.uploadBlocked) this.scheduleUpload(next);
        });
    }, 2000);
  }
  private onOnline = (): void => {
    this.uploadBlocked = false;
    for (const capture of this.snapshot.captures)
      if (capture.state !== "finished") this.scheduleUpload(capture.id);
  };
  private beforeUnload = (event: BeforeUnloadEvent): void => {
    if (this.snapshot.activeId || this.snapshot.pendingLocalChunks > 0) {
      event.preventDefault();
      event.returnValue = "";
    }
  };
  private async releaseAudio(): Promise<void> {
    for (const track of this.stream?.getTracks() ?? []) track.stop();
    this.stream = undefined;
    this.node?.disconnect();
    this.node = undefined;
    if (this.context && this.context.state !== "closed")
      await this.context.close().catch(() => {});
    this.context = undefined;
    this.releaseLease?.();
    this.releaseLease = undefined;
  }
  activate(): void {
    this.lifetime++;
    this.closed = false;
    window.addEventListener("online", this.onOnline);
    window.addEventListener("beforeunload", this.beforeUnload);
  }
  async prepareLogout(): Promise<void> {
    if (this.snapshot.activeId) await this.pause("stopped");
    if (this.snapshot.pendingLocalChunks > 0)
      throw new Error(
        "Перед выходом повторите локальное сохранение. Несохранённый звук пока находится в этой вкладке.",
      );
  }
  async close(): Promise<boolean> {
    const generation = ++this.lifetime;
    this.closed = true;
    if (this.uploadTimer) clearTimeout(this.uploadTimer);
    this.uploadTimer = undefined;
    window.removeEventListener("online", this.onOnline);
    try {
      await this.pause("stopped");
    } catch {
      /* pending PCM stays in this owner-bound worker */
    }
    await this.releaseAudio();
    if (this.lifetime !== generation) return false;
    if (this.snapshot.pendingLocalChunks > 0) return false;
    window.removeEventListener("beforeunload", this.beforeUnload);
    this.client.close();
    this.listeners.clear();
    return true;
  }
}
